import { keepPreviousData } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import { useEffect, useState } from 'react'

import { MarketSummaryDtoCursorPaginatedResponse, useGetAllClientMarkets } from '@/gen/publicApi'

export default function useGetAllClientsMarketsInfinite(clientCode: string, includeDeleted: boolean) {
    const [cursor, setCursor] = useState<string | undefined>(undefined)
    const [pages, setPages] = useState<MarketSummaryDtoCursorPaginatedResponse['data']>([])
    const [nextCursor, setNextCursor] = useState<string | undefined>(undefined)
    const { data, isLoading } = useGetAllClientMarkets(
        clientCode,
        {
            Cursor: cursor,
            includeDeleted,
        },
        {
            query: {
                placeholderData: keepPreviousData,
            },
        },
    )

    // Reset pages and cursor when clientCode or includeDeleted changes
    useEffect(() => {
        setPages([])
        setCursor(undefined)
    }, [clientCode, includeDeleted])

    useEffect(() => {
        if (!data) {
            return
        }

        setNextCursor(data?.nextCursor || undefined)

        // Concatenate data only when the cursor changes (for pagination)
        if (cursor) {
            const newData = pages?.concat(data.data || [])
            const newPages = uniqBy(newData, page => page.code)
            setPages(newPages)
        } else {
            // If cursor is undefined (first fetch or reset), replace the data
            setPages(data.data || [])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, cursor])

    const fetchNextPage = () => {
        if (!nextCursor) {
            return
        }

        setCursor(nextCursor)
    }

    return {
        data: pages,
        isLoading,
        fetchNextPage,
        hasNextPage: nextCursor !== undefined,
    }
}
